import { skipToken, useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { domainCodeAtom } from '@australiangreens/ag-internal-components';
import { useFeatureToggle } from '@/featureToggles/FeatureTogglesContext';
import { useSearchPreviewsPaused } from '@/hooks/useSearchPreviewsPaused';
import { SearchPreviewData } from '@/types';
import { ExtendedSearchTreeNode } from '@/types/searchTreeTypes';
import callBackendApi, { useCallBackendApi } from './call';

/**
 * Will not retrieve data until searchTree is defined.
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/disabling-queries#typesafe-disabling-of-queries-using-skiptoken
 */
export const useSearchPreviewQuery = (searchTree: ExtendedSearchTreeNode | undefined) => {
  const domainCode = useAtomValue(domainCodeAtom);
  const callBackendApiFn = useCallBackendApi<never, SearchPreviewData>(
    'Starting Advanced Search',
    'Finished Advanced Search'
  );
  const printTime = useFeatureToggle('Print Advanced Search Time');
  const useAdvancedSearchII = useFeatureToggle('Use Advanced Search II');
  const previewsPaused = useSearchPreviewsPaused();
  if (searchTree) {
    // eslint-disable-next-line react-hooks/immutability -- stamp printTime onto the tree for the preview request
    searchTree.printTime = printTime;
    // eslint-disable-next-line react-hooks/immutability -- stamp Use Advanced Search II onto the tree for the preview request
    searchTree.useAdvancedSearchII = useAdvancedSearchII;
  }

  return useQuery({
    queryKey: ['advsearch', 'preview', domainCode, searchTree],
    // TODO: Do we need to worry about searchTree being undefined or is that better handled in another manner?
    queryFn:
      searchTree && !previewsPaused
        ? async ({ signal }) =>
            callBackendApiFn({
              method: 'get',
              endpoint: `/advsearch/preview?domainCode=${domainCode}&tree=${encodeURIComponent(
                JSON.stringify(searchTree)
              )}`,
              abortSignal: signal,
            })
        : skipToken,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    // staleTime: 20000, // ! Not sure if we want this
    // staleTime: Infinity,
    retry: false,
    // initialData: [],
  });
};

type PrecacheResponse = {
  count?: number; // Will change this later
};

export const precacheSearchTree = async (
  token: string,
  searchTree: ExtendedSearchTreeNode,
  domainCode: string
) => {
  const treeParam = encodeURIComponent(JSON.stringify(searchTree));

  const data = await callBackendApi({
    token,
    method: 'post',
    endpoint: `/advsearch/precache?domainCode=${domainCode}&tree=${treeParam}`,
  });

  return data as PrecacheResponse;
};
